import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import RightBar from '../components/layout/RightBar';
import ProfileHeader from '../components/user/ProfileHeader';
import ProfileTabs from '../components/user/ProfileTabs';
import PostCard from '../components/posts/PostCard';
import Loader from '../components/common/Loader';
import Button from '../components/common/Button';
import ExperienceForm from '../components/user/ExperienceForm';
import EducationForm from '../components/user/EducationForm';
import useAuth from '../hooks/useAuth';
import useUser from '../hooks/useUser';
import { getUserByIdOrUsername, followUser, unfollowUser } from '../api/users';
import { getUserPosts } from '../api/posts';

/**
 * Profile page for viewing user profiles
 * @returns {JSX.Element} Profile page component
 */
const Profile = () => {
  const { username } = useParams();
  const { user: currentUserResponse, isAuth, loading: authLoading, refreshUser } = useAuth();

  // State for inline editing
  const [editMode, setEditMode] = useState({
    name: false,
    bio: false,
    skills: false,
    experience: false,
    education: false,
  });

  const [editData, setEditData] = useState({
    name: '',
    bio: '',
    skills: [],
    newSkill: '',
  });

  const [showExperienceForm, setShowExperienceForm] = useState(false);
  const [currentExperience, setCurrentExperience] = useState(null);

  const [showEducationForm, setShowEducationForm] = useState(false);
  const [currentEducation, setCurrentEducation] = useState(null);

  // Extract the actual user data from the response structure
  // Handle all possible formats and nesting levels
  const extractUserData = (userResponse) => {
    if (!userResponse) return null;

    // Handle various possible structures
    if (userResponse.data && userResponse.data.user) {
      return userResponse.data.user;
    } else if (userResponse.data) {
      return userResponse.data;
    } else if (userResponse.user) {
      return userResponse.user;
    } else if (userResponse.username) {
      return userResponse;
    }

    return null;
  };

  const currentUser = extractUserData(currentUserResponse);

  const [profileUser, setProfileUser] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isProfileOwner, setIsProfileOwner] = useState(false);
  const [userPosts, setUserPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('posts');
  const [loading, setLoading] = useState({
    user: true,
    posts: false,
  });
  const [error, setError] = useState({
    user: '',
    posts: '',
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Initialize user editing functions
  const {
    updateProfile,
    uploadProfileImage,
    uploadCoverImage,
    updateExperience,
    updateEducation,
    loading: userActionLoading,
    error: userActionError,
    uploadProgress,
  } = useUser(profileUser, (updatedUser) => {
    // Update the local user state when profile is updated
    setProfileUser(updatedUser);
    // Refresh current user if this is the profile owner
    if (isProfileOwner && refreshUser) {
      refreshUser();
    }
  });

  // Force refresh user data when component mounts if authenticated
  useEffect(() => {
    if (isAuth && (!currentUserResponse || typeof currentUserResponse === 'undefined')) {
      refreshUser && refreshUser();
    }
  }, [isAuth, currentUserResponse, refreshUser]);

  // Check if viewing own profile based on URL
  useEffect(() => {
    if (isAuth && username) {
      // If we're on profile/me or we know this is current user's username,
      // set as profile owner immediately
      if (
        username === 'me' ||
        (currentUser && currentUser.username?.toLowerCase() === username.toLowerCase())
      ) {
        setIsProfileOwner(true);
      }
    }
  }, [username, isAuth, currentUser]);

  // Initialize edit data when profile user changes
  useEffect(() => {
    if (profileUser) {
      setEditData({
        name: profileUser.name || '',
        bio: profileUser.bio || '',
        skills: profileUser.skills || [],
        newSkill: '',
      });
    }
  }, [profileUser]);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!username) return;

      try {
        setLoading((prev) => ({ ...prev, user: true }));
        setError((prev) => ({ ...prev, user: '' }));

        const response = await getUserByIdOrUsername(username);
        const userData = response.data.user || (response.data.data && response.data.data.user);

        if (userData) {
          setProfileUser(userData);

          // Handle special case for /profile/me - always current user
          if (username === 'me') {
            setIsProfileOwner(true);
          }
          // General ownership check if we have current user data
          else if (isAuth && currentUser) {
            // Multiple checks for maximum compatibility
            const isOwner =
              // By ID
              (currentUser._id && userData._id && currentUser._id === userData._id) ||
              // By username (case-insensitive)
              (currentUser.username &&
                userData.username &&
                currentUser.username.toLowerCase() === userData.username.toLowerCase());

            setIsProfileOwner(isOwner);
          }

          // Only check following if not owner
          if (!isProfileOwner) {
            if (response.data.isFollowing !== undefined) {
              setIsFollowing(response.data.isFollowing);
            } else if (response.data.data && response.data.data.isFollowing !== undefined) {
              setIsFollowing(response.data.data.isFollowing);
            } else if (userData.followers && currentUser._id) {
              setIsFollowing(userData.followers.includes(currentUser._id));
            } else {
              setIsFollowing(false);
            }
          }

          setActiveTab('posts');
          setPage(1);
        } else {
          setError((prev) => ({
            ...prev,
            user: 'Failed to load user profile. No user data found.',
          }));
        }
      } catch (error) {
        setError((prev) => ({
          ...prev,
          user: 'Failed to load user profile. Please try again.',
        }));
      } finally {
        setLoading((prev) => ({ ...prev, user: false }));
      }
    };

    fetchUserData();
  }, [username, isAuth, currentUser]);

  // Fetch posts when profile user changes or active tab is posts
  useEffect(() => {
    if (profileUser && activeTab === 'posts') {
      fetchUserPosts(1);
    }
  }, [profileUser, activeTab]);

  // Handlers for editing fields
  const startEditing = (field) => {
    setEditMode((prev) => ({ ...prev, [field]: true }));
  };

  const cancelEditing = (field) => {
    setEditMode((prev) => ({ ...prev, [field]: false }));
    // Reset to original values
    if (profileUser) {
      setEditData((prev) => ({
        ...prev,
        [field]: profileUser[field] || '',
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSkillInputChange = (e) => {
    setEditData((prev) => ({ ...prev, newSkill: e.target.value }));
  };

  const addSkill = () => {
    if (editData.newSkill.trim() && !editData.skills.includes(editData.newSkill.trim())) {
      setEditData((prev) => ({
        ...prev,
        skills: [...prev.skills, prev.newSkill.trim()],
        newSkill: '',
      }));
    }
  };

  const removeSkill = (skill) => {
    setEditData((prev) => ({
      ...prev,
      skills: prev.skills.filter((s) => s !== skill),
    }));
  };

  const saveField = async (field) => {
    try {
      const updateData = {};
      updateData[field] = editData[field];

      await updateProfile(updateData);
      setEditMode((prev) => ({ ...prev, [field]: false }));
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
    }
  };

  // Handle experience and education forms
  const handleAddExperience = () => {
    setCurrentExperience(null);
    setShowExperienceForm(true);
  };

  const handleEditExperience = (experience) => {
    setCurrentExperience(experience);
    setShowExperienceForm(true);
  };

  const handleSaveExperience = async (experienceData) => {
    try {
      // Create a copy of the current experience array
      const updatedExperiences = [...(profileUser.experience || [])];

      if (currentExperience) {
        // Find the index of the experience to update
        const index = updatedExperiences.findIndex(
          (exp) =>
            exp._id === currentExperience._id ||
            (exp.company === currentExperience.company &&
              exp.title === currentExperience.title &&
              exp.from === currentExperience.from)
        );

        if (index !== -1) {
          // Update existing experience
          updatedExperiences[index] = { ...updatedExperiences[index], ...experienceData };
        }
      } else {
        // Add new experience
        updatedExperiences.push(experienceData);
      }

      // Send update to the server
      await updateExperience(updatedExperiences);

      // Close the form
      setShowExperienceForm(false);
      setCurrentExperience(null);
    } catch (error) {
      console.error('Failed to save experience:', error);
    }
  };

  const handleDeleteExperience = async (experienceToDelete) => {
    try {
      // Filter out the experience to delete
      const updatedExperiences = profileUser.experience.filter(
        (exp) =>
          exp._id !== experienceToDelete._id &&
          !(
            exp.company === experienceToDelete.company &&
            exp.title === experienceToDelete.title &&
            exp.from === experienceToDelete.from
          )
      );

      // Send update to the server
      await updateExperience(updatedExperiences);
    } catch (error) {
      console.error('Failed to delete experience:', error);
    }
  };

  const handleAddEducation = () => {
    setCurrentEducation(null);
    setShowEducationForm(true);
  };

  const handleEditEducation = (education) => {
    setCurrentEducation(education);
    setShowEducationForm(true);
  };

  const handleSaveEducation = async (educationData) => {
    try {
      // Create a copy of the current education array
      const updatedEducations = [...(profileUser.education || [])];

      if (currentEducation) {
        // Find the index of the education to update
        const index = updatedEducations.findIndex(
          (edu) =>
            edu._id === currentEducation._id ||
            (edu.school === currentEducation.school &&
              edu.degree === currentEducation.degree &&
              edu.from === currentEducation.from)
        );

        if (index !== -1) {
          // Update existing education
          updatedEducations[index] = { ...updatedEducations[index], ...educationData };
        }
      } else {
        // Add new education
        updatedEducations.push(educationData);
      }

      // Send update to the server
      await updateEducation(updatedEducations);

      // Close the form
      setShowEducationForm(false);
      setCurrentEducation(null);
    } catch (error) {
      console.error('Failed to save education:', error);
    }
  };

  const handleDeleteEducation = async (educationToDelete) => {
    try {
      // Filter out the education to delete
      const updatedEducations = profileUser.education.filter(
        (edu) =>
          edu._id !== educationToDelete._id &&
          !(
            edu.school === educationToDelete.school &&
            edu.degree === educationToDelete.degree &&
            edu.from === educationToDelete.from
          )
      );

      // Send update to the server
      await updateEducation(updatedEducations);
    } catch (error) {
      console.error('Failed to delete education:', error);
    }
  };

  // Fetch user posts
  const fetchUserPosts = async (pageNum) => {
    if (!profileUser) return;

    try {
      setLoading((prev) => ({ ...prev, posts: true }));
      setError((prev) => ({ ...prev, posts: '' }));

      const response = await getUserPosts(profileUser._id, {
        page: pageNum,
        limit: 10,
      });

      if (pageNum === 1) {
        setUserPosts(response.data.data.posts);
      } else {
        setUserPosts((prev) => [...prev, ...response.data.data.posts]);
      }

      // Check if there are more posts to load
      setHasMore(response.data.pagination.page < response.data.pagination.pages);
      setPage(response.data.pagination.page);
    } catch (error) {
      setError((prev) => ({
        ...prev,
        posts: 'Failed to load posts. Please try again.',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, posts: false }));
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle follow/unfollow
  const handleFollowChange = async (newFollowState) => {
    if (!isAuth || !profileUser) return;

    try {
      if (newFollowState) {
        await followUser(profileUser._id);
      } else {
        await unfollowUser(profileUser._id);
      }

      setIsFollowing(newFollowState);

      // Update followers count
      setProfileUser((prev) => ({
        ...prev,
        followersCount: newFollowState
          ? (prev.followersCount || 0) + 1
          : (prev.followersCount || 1) - 1,
      }));
    } catch (error) {}
  };

  // Load more posts
  const handleLoadMore = () => {
    if (hasMore && !loading.posts) {
      fetchUserPosts(page + 1);
    }
  };

  // Render loading state
  if (loading.user) {
    return (
      <div>
        {/* <Header /> */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center py-12">
            <Loader size="lg" />
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error.user) {
    return (
      <div>
        {/* <Header /> */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error.user}
          </div>
        </div>
      </div>
    );
  }

  // Render profile not found
  if (!profileUser) {
    return (
      <div>
        {/* <Header /> */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">User not found</h3>
            <p className="mt-1 text-sm text-gray-500">
              The user you're looking for doesn't exist or has been removed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      {/* <Header /> */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left sidebar */}

          {/* Main content */}
          <div className="flex-1 space-y-6">
            {/* Profile header */}
            <ProfileHeader
              profileUser={profileUser}
              isFollowing={isFollowing}
              onFollowChange={handleFollowChange}
              isProfileOwner={isProfileOwner}
              onUpdateProfile={updateProfile}
              onUploadProfileImage={uploadProfileImage}
              onUploadCoverImage={uploadCoverImage}
              loading={userActionLoading}
              error={userActionError}
              uploadProgress={uploadProgress}
            />

            {/* Profile tabs */}
            <ProfileTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              showAbout={true}
              showExperience={true}
            />

            {/* Tab content */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              {/* Posts tab */}
              {activeTab === 'posts' && (
                <div className="space-y-6">
                  {error.posts && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {error.posts}
                    </div>
                  )}

                  {loading.posts && userPosts.length === 0 ? (
                    <div className="flex justify-center py-12">
                      <Loader size="lg" />
                    </div>
                  ) : userPosts.length === 0 ? (
                    <div className="text-center py-12">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                      </svg>
                      <h3 className="mt-2 text-lg font-medium text-gray-900">No posts yet</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {profileUser._id === currentUser?._id
                          ? "You haven't created any posts yet."
                          : `${profileUser.name} hasn't created any posts yet.`}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-4">
                        {userPosts.map((post) => (
                          <PostCard key={post._id} post={post} />
                        ))}
                      </div>

                      {hasMore && (
                        <div className="flex justify-center mt-6">
                          <Button
                            onClick={handleLoadMore}
                            variant="light"
                            isLoading={loading.posts}
                          >
                            Load More
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* About tab */}
              {activeTab === 'about' && (
                <div className="space-y-6">
                  {/* Bio section */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-lg font-medium text-gray-900">About</h3>
                      {isProfileOwner && !editMode.bio && (
                        <button
                          onClick={() => startEditing('bio')}
                          className="text-blue-500 hover:text-blue-600 flex items-center"
                        >
                          <svg
                            className="h-4 w-4 mr-1"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                          Edit
                        </button>
                      )}
                    </div>

                    {editMode.bio && isProfileOwner ? (
                      <div className="space-y-3">
                        <textarea
                          name="bio"
                          value={editData.bio}
                          onChange={handleInputChange}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Write something about yourself..."
                        />
                        <div className="flex justify-end space-x-2">
                          <Button size="sm" variant="light" onClick={() => cancelEditing('bio')}>
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => saveField('bio')}
                            isLoading={userActionLoading.profile}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {profileUser.bio || 'No bio available.'}
                      </p>
                    )}
                  </div>

                  {/* Skills section */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-lg font-medium text-gray-900">Skills</h3>
                      {isProfileOwner && !editMode.skills && (
                        <button
                          onClick={() => startEditing('skills')}
                          className="text-blue-500 hover:text-blue-600 flex items-center"
                        >
                          <svg
                            className="h-4 w-4 mr-1"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                          {profileUser.skills && profileUser.skills.length > 0 ? 'Edit' : 'Add'}
                        </button>
                      )}
                    </div>

                    {editMode.skills && isProfileOwner ? (
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={editData.newSkill}
                            onChange={handleSkillInputChange}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Add a skill..."
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addSkill();
                              }
                            }}
                          />
                          <Button size="sm" onClick={addSkill}>
                            Add
                          </Button>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-2">
                          {editData.skills.map((skill, index) => (
                            <div
                              key={index}
                              className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm flex items-center"
                            >
                              {skill}
                              <button
                                onClick={() => removeSkill(skill)}
                                className="ml-1 text-gray-500 hover:text-red-500"
                              >
                                <svg
                                  className="h-4 w-4"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="flex justify-end space-x-2">
                          <Button size="sm" variant="light" onClick={() => cancelEditing('skills')}>
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => saveField('skills')}
                            isLoading={userActionLoading.profile}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : profileUser.skills && profileUser.skills.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {profileUser.skills.map((skill, index) => (
                          <span
                            key={index}
                            className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No skills listed.</p>
                    )}
                  </div>

                  {/* Contact info section */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-lg font-medium text-gray-900">Contact</h3>
                      {/* {isProfileOwner && (
                        <Button variant="light" size="sm" as="a" href="/settings/profile">
                          Edit Contact
                        </Button>
                      )} */}
                    </div>
                    <div className="space-y-2">
                      {profileUser.email && (
                        <div className="flex items-center">
                          <svg
                            className="h-5 w-5 text-gray-400 mr-2"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                          <span className="text-gray-700">{profileUser.email}</span>
                        </div>
                      )}
                      {profileUser.website && (
                        <div className="flex items-center">
                          <svg
                            className="h-5 w-5 text-gray-400 mr-2"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                            />
                          </svg>
                          <a
                            href={
                              profileUser.website.startsWith('http')
                                ? profileUser.website
                                : `https://${profileUser.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {profileUser.website}
                          </a>
                        </div>
                      )}
                      {profileUser.location && (
                        <div className="flex items-center">
                          <svg
                            className="h-5 w-5 text-gray-400 mr-2"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <span className="text-gray-700">{profileUser.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Comments tab */}
              {activeTab === 'comments' && (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </svg>
                  <h3 className="mt-2 text-lg font-medium text-gray-900">Comments</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Comment history will be available soon.
                  </p>
                </div>
              )}

              {/* Experience tab */}
              {activeTab === 'experience' && (
                <div className="space-y-6">
                  {/* Experience section */}
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900">Professional Experience</h3>
                      {isProfileOwner && (
                        <Button variant="light" size="sm" onClick={handleAddExperience}>
                          Add Experience
                        </Button>
                      )}
                    </div>

                    {/* Experience form */}
                    {showExperienceForm && (
                      <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h4 className="text-lg font-medium text-gray-800 mb-4">
                          {currentExperience ? 'Edit Experience' : 'Add Experience'}
                        </h4>
                        <ExperienceForm
                          experience={currentExperience}
                          onSave={handleSaveExperience}
                          onCancel={() => setShowExperienceForm(false)}
                        />
                      </div>
                    )}

                    {profileUser.experience && profileUser.experience.length > 0 ? (
                      <div className="space-y-8">
                        {profileUser.experience.map((exp, index) => (
                          <div key={index} className="border-l-4 border-blue-100 pl-4 py-1">
                            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-1">
                              <h4 className="font-medium text-gray-900">
                                {exp.title} at {exp.company}
                              </h4>
                              <span className="text-sm text-gray-500">
                                {new Date(exp.from).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                })}{' '}
                                -{' '}
                                {exp.current
                                  ? 'Present'
                                  : exp.to
                                  ? new Date(exp.to).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                    })
                                  : ''}
                              </span>
                            </div>
                            {exp.location && (
                              <div className="text-sm text-gray-600 mb-2">{exp.location}</div>
                            )}
                            {exp.description && (
                              <p className="text-gray-700 whitespace-pre-wrap">{exp.description}</p>
                            )}

                            {/* Edit/Delete buttons for experience */}
                            {isProfileOwner && (
                              <div className="flex mt-3 space-x-2">
                                <button
                                  onClick={() => handleEditExperience(exp)}
                                  className="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center"
                                >
                                  <svg
                                    className="h-3.5 w-3.5 mr-1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                    />
                                  </svg>
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteExperience(exp)}
                                  className="text-red-600 hover:text-red-800 text-sm inline-flex items-center"
                                >
                                  <svg
                                    className="h-3.5 w-3.5 mr-1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">
                          No experience listed
                        </h3>
                        {isProfileOwner && (
                          <p className="mt-1 text-sm text-gray-500">
                            Add your professional experience to showcase your career history.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Education section */}
                  <div className="mt-8">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900">Education</h3>
                      {isProfileOwner && (
                        <Button variant="light" size="sm" onClick={handleAddEducation}>
                          Add Education
                        </Button>
                      )}
                    </div>

                    {/* Education form */}
                    {showEducationForm && (
                      <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h4 className="text-lg font-medium text-gray-800 mb-4">
                          {currentEducation ? 'Edit Education' : 'Add Education'}
                        </h4>
                        <EducationForm
                          education={currentEducation}
                          onSave={handleSaveEducation}
                          onCancel={() => setShowEducationForm(false)}
                        />
                      </div>
                    )}

                    {profileUser.education && profileUser.education.length > 0 ? (
                      <div className="space-y-8">
                        {profileUser.education.map((edu, index) => (
                          <div key={index} className="border-l-4 border-green-100 pl-4 py-1">
                            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-1">
                              <h4 className="font-medium text-gray-900">
                                {edu.degree}, {edu.school}
                              </h4>
                              <span className="text-sm text-gray-500">
                                {new Date(edu.from).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                })}{' '}
                                -{' '}
                                {edu.current
                                  ? 'Present'
                                  : edu.to
                                  ? new Date(edu.to).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                    })
                                  : ''}
                              </span>
                            </div>
                            {edu.fieldOfStudy && (
                              <div className="text-sm text-gray-600 mb-2">{edu.fieldOfStudy}</div>
                            )}
                            {edu.description && (
                              <p className="text-gray-700 whitespace-pre-wrap">{edu.description}</p>
                            )}

                            {/* Edit/Delete buttons for education */}
                            {isProfileOwner && (
                              <div className="flex mt-3 space-x-2">
                                <button
                                  onClick={() => handleEditEducation(edu)}
                                  className="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center"
                                >
                                  <svg
                                    className="h-3.5 w-3.5 mr-1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                    />
                                  </svg>
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteEducation(edu)}
                                  className="text-red-600 hover:text-red-800 text-sm inline-flex items-center"
                                >
                                  <svg
                                    className="h-3.5 w-3.5 mr-1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">
                          No education listed
                        </h3>
                        {isProfileOwner && (
                          <p className="mt-1 text-sm text-gray-500">
                            Add your educational background to showcase your qualifications.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Communities tab */}
              {activeTab === 'communities' && (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <h3 className="mt-2 text-lg font-medium text-gray-900">Communities</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Community membership information will be available soon.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right sidebar */}
          <div className="hidden lg:block lg:w-80">
            <RightBar />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;